import { supabase } from '../lib/supabase.js';
import { isAdmin } from '../lib/auth.js';
import { calcLeadTime, getStribroRoute } from '../utils/planovac.js';
import { uploadTechPhoto, uploadFiles } from '../lib/photos.js';
import { openLightbox } from '../lib/lightbox.js';
import { getMediaType, mediaThumbHTML, ACCEPT_ALL_MEDIA } from '../lib/media.js';

async function getSignedUrl(path) {
    if (!path) return null;
    const { data } = await supabase.storage.from('photos-tech').createSignedUrl(path, 3600);
    return data?.signedUrl || null;
}

const POWER_TYPES = [
    '400 V · 5pin · 16 A',
    '400 V · 4pin · 16 A',
    '400 V · 5pin · 32 A',
    '400 V · 4pin · 32 A',
    '220 V',
    'kombinace 230 V + 400 V přípojka',
    'jiné / kombinace'
];
const POWER_SIDES = ['Levá', 'Pravá', 'Obě strany'];
const CABLES_400 = ['žádný', '3 m', '5 m', '10 m', '20 m'];
const CABLES_220 = ['žádný', '25 m', '50 m'];
const PA_VARIANTS = ['1 sub', '2 SUB kardio', '2 SUB Inline Kardio'];
const TOP_BOX_ANGLES = ['Stativ rovně', 'Stativ malý náklon', 'Stativ velký náklon', 'Zavěšeno'];
const SUB_PLACEMENTS = ['Před jevištěm', 'Pod jevištěm'];
const MOVING_ALL = [
    'Vozíky na jeviště',
    'Vozíky před pódium',
    'Patro',
    'Ručně',
    'Výtah',
    'Vozíky dál',
    'Kopec',
    'Druhá plošina'
];

let settingsCache = null;
async function getSettings() {
    if (settingsCache) return settingsCache;
    const { data } = await supabase.from('settings').select('key, value');
    settingsCache = Object.fromEntries((data || []).map(s => [s.key, s.value]));
    return settingsCache;
}

function esc(s) {
    if (s == null) return '';
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
function opt(value, current) {
    return `<option${value === current ? ' selected' : ''}>${esc(value)}</option>`;
}

function renderDisplay(place, settings) {
    const env = place.environment === 'venku'
        ? '<span class="tag" style="background:#e0eaef;color:var(--tech);">Venku</span>'
        : 'Uvnitř';
    const distance = `${place.distance_pilsen || 0} km od Plzně<span class="dot">·</span>${place.distance_stribro || 0} km od Stříbra`;

    const lead = calcLeadTime(place, settings);
    const km1 = place.distance_pilsen || 0;
    const km2 = place.distance_stribro || 0;
    const { breakdown: bd } = lead;
    const route = getStribroRoute(place, settings);
    const ROUTE_LABELS = { 'via_pilsen': 'Přes Plzeň – přesed do dodávky', 'via_stribro': 'Přes Stříbro – přesed do dodávky', 'direct': 'Přímo ze Stříbra' };
    const routeLabel = (ROUTE_LABELS[route] || 'Přímo ze Stříbra') + (place.stribro_route ? '' : ' (auto)');
    const over = (col, def) => place[col] != null
        ? `<td class="num" title="přepsáno pro toto místo">${place[col]} min ✎</td>`
        : `<td class="num">${def} min</td>`;
    const placeExtra = place.extra_reserve_min ?? 0;
    const timeRow = `
        <table class="time-breakdown">
            <thead><tr><th>Položka</th><th>Plzeň</th><th>Stříbro</th></tr></thead>
            <tbody>
                <tr><td>Rezerva začátek</td>${over('reserve_start_min', bd.reserveStart)}${over('reserve_start_min', bd.reserveStart)}</tr>
                <tr><td>Stavba jeviště</td>${over('stage_setup_min', bd.stageSetup)}${over('stage_setup_min', bd.stageSetup)}</tr>
                <tr><td>Stěhování</td>${over('moving_min', bd.moving)}${over('moving_min', bd.moving)}</tr>
                ${placeExtra ? `<tr><td>Rezerva místa</td><td class="num">${placeExtra} min</td><td class="num">${placeExtra} min</td></tr>` : ''}
                <tr><td>Cesta (${km1} / ${km2} km)</td><td class="num">${bd.travelPilsen} min</td><td class="num">${bd.travelStribro} min</td></tr>
                <tr><td>Rezerva odjezdu</td>${over('reserve_depart_min', bd.reserveDepart)}${over('reserve_depart_min', bd.reserveDepart)}</tr>
                <tr class="total"><td>Předstih celkem</td><td class="num">${lead.pilsenMin} min</td><td class="num">${lead.stribroMin} min</td></tr>
            </tbody>
        </table>
    `;

    const power = [place.power_type, place.power_side].filter(Boolean).join('<span class="dot">·</span>');
    const cables = [];
    if (place.cable_400v && place.cable_400v !== 'žádný') cables.push(`400 V: ${esc(place.cable_400v)}`);
    if (place.cable_220v && place.cable_220v !== 'žádný') cables.push(`220 V: ${esc(place.cable_220v)}`);

    const moving = (place.moving_options || []).map(o => `<span class="tag">${esc(o)}</span>`).join('');
    const carpet = place.has_carpet
        ? '<span class="display-value success-text">✓ Na místě (nemusíme brát)</span>'
        : '<span class="display-value muted">není – musíme brát vlastní</span>';

    const topBox = place.top_box_angle ? `<div class="display-row"><span class="display-label">CS10P:</span><span class="display-value">${esc(place.top_box_angle)}</span></div>` : '';
    const subInfo = [];
    if (place.sub_placement) subInfo.push(esc(place.sub_placement));
    if (place.sub_delay_ms) subInfo.push(`${place.sub_delay_ms}ms`);
    const subRow = subInfo.length ? `<div class="display-row"><span class="display-label">SUB fázování</span><span class="display-value">${subInfo.join('<span class="dot">·</span>')}</span></div>` : '';

    return `
        <div class="place-display">
            <div class="display-row"><span class="display-label">Prostředí</span><span class="display-value">${env}</span></div>
            <div class="display-row"><span class="display-label">Vzdálenost</span><span class="display-value">${distance}</span></div>
            <div class="display-row"><span class="display-label">Trasa Stříbro</span><span class="display-value">${routeLabel}</span></div>
            <div class="display-row"><span class="display-label">Časový plán</span><span class="display-value">${timeRow}</span></div>
            ${power ? `<div class="display-row"><span class="display-label">Zdroj el.</span><span class="display-value">${power || '—'}</span></div>` : ''}
            ${cables.length ? `<div class="display-row"><span class="display-label">Prodlužovák</span><span class="display-value">${cables.join('<span class="dot">·</span>')}</span></div>` : ''}
            ${place.pa_variant ? `<div class="display-row"><span class="display-label">Ozvučení</span><span class="display-value">${esc(place.pa_variant)}</span></div>` : ''}
            ${topBox}
            ${subRow}
            ${moving ? `<div class="display-row"><span class="display-label">Stěhování</span><span class="display-value">${moving}</span></div>` : ''}
            <div class="display-row"><span class="display-label">Koberec</span>${carpet}</div>
        </div>
    `;
}

function renderEditForm(place) {
    const moving = place.moving_options || [];
    return `
        <div class="spec-section">
            <h4>Název místa</h4>
            <div class="spec-row" style="grid-template-columns:1fr;">
                <input type="text" name="name" value="${esc(place.name || '')}" required
                       placeholder="např. Cebiv KD"
                       style="width:100%;padding:7px 10px;border:1px solid var(--border);border-radius:4px;font-family:inherit;font-size:14px;font-weight:600;">
            </div>
        </div>
        <div class="spec-section">
            <h4>Prostředí</h4>
            <div class="spec-row">
                <label>Typ akce</label>
                <select name="environment">
                    <option value="uvnitř"${place.environment === 'uvnitř' ? ' selected' : ''}>Uvnitř</option>
                    <option value="venku"${place.environment === 'venku' ? ' selected' : ''}>Venku</option>
                </select>
            </div>
        </div>
        <div class="spec-section">
            <h4>Vzdálenost</h4>
            <div class="spec-row"><label>Od Plzně <span class="unit">km</span></label><input type="number" name="distance_pilsen" value="${place.distance_pilsen ?? 0}"></div>
            <div class="spec-row"><label>Od Stříbra <span class="unit">km</span></label><input type="number" name="distance_stribro" value="${place.distance_stribro ?? 0}"></div>
        </div>
        <div class="spec-section">
            <h4>Trasa ze Stříbra</h4>
            <div class="spec-row">
                <label>Způsob jízdy</label>
                <select name="stribro_route">
                    <option value="">Automaticky (dle vzdálenosti)</option>
                    <option value="via_pilsen"${place.stribro_route === 'via_pilsen' ? ' selected' : ''}>Přes Plzeň – přesed do dodávky</option>
                    <option value="via_stribro"${place.stribro_route === 'via_stribro' ? ' selected' : ''}>Přes Stříbro – přesed do dodávky</option>
                    <option value="direct"${place.stribro_route === 'direct' ? ' selected' : ''}>Přímo ze Stříbra</option>
                </select>
            </div>
        </div>
        <div class="spec-section">
            <h4>Časový plán – čas cesty + rezerva místa</h4>
            <div class="spec-row"><label>Čas cesty z Plzně <span class="unit">min</span></label><input type="number" name="travel_time_pilsen" value="${place.travel_time_pilsen ?? ''}" placeholder="auto = km × 1"></div>
            <div class="spec-row"><label>Čas cesty ze Stříbra <span class="unit">min</span></label><input type="number" name="travel_time_stribro" value="${place.travel_time_stribro ?? ''}" placeholder="auto = km × 1"></div>
            <div class="spec-row"><label>Rezerva místa navíc <span class="unit">schodiště, špatný přístup, akustika</span></label>
                <div class="extra-reserve-control">
                    <label class="check-inline"><input type="checkbox" name="extra_reserve_active"${Number(place.extra_reserve_min) > 0 ? ' checked' : ''}> aktivní</label>
                    <input type="number" name="extra_reserve_min" value="${Number(place.extra_reserve_min) || 15}" ${Number(place.extra_reserve_min) > 0 ? '' : 'disabled'}>
                    <span class="unit">min</span>
                </div>
            </div>
        </div>
        <div class="spec-section">
            <h4>Přepis časů přípravy <span style="font-weight:400;color:var(--text-muted);font-size:12px;">prázdné = použij výchozí</span></h4>
            <div class="spec-row"><label>Rezerva začátek <span class="unit">výchozí 20 min</span></label><input type="number" name="reserve_start_min" value="${place.reserve_start_min ?? ''}" placeholder="20" min="0" style="width:90px;"></div>
            <div class="spec-row"><label>Stavba jeviště <span class="unit">výchozí 40 min</span></label><input type="number" name="stage_setup_min" value="${place.stage_setup_min ?? ''}" placeholder="40" min="0" style="width:90px;"></div>
            <div class="spec-row"><label>Stěhování <span class="unit">výchozí 10 min</span></label><input type="number" name="moving_min" value="${place.moving_min ?? ''}" placeholder="10" min="0" style="width:90px;"></div>
            <div class="spec-row"><label>Rezerva odjezdu <span class="unit">výchozí 5 min</span></label><input type="number" name="reserve_depart_min" value="${place.reserve_depart_min ?? ''}" placeholder="5" min="0" style="width:90px;"></div>
        </div>
        <div class="spec-section">
            <h4>Zdroj elektřiny</h4>
            <div class="spec-row"><label>Typ zdroje</label><select name="power_type">${POWER_TYPES.map(t => opt(t, place.power_type)).join('')}</select></div>
            <div class="spec-row"><label>Strana jeviště</label><select name="power_side"><option value=""></option>${POWER_SIDES.map(s => opt(s, place.power_side)).join('')}</select></div>
            <div class="spec-row"><label>Prodlužovák 400 V</label><select name="cable_400v">${CABLES_400.map(c => opt(c, place.cable_400v)).join('')}</select></div>
            <div class="spec-row"><label>Prodlužovák 220 V</label><select name="cable_220v">${CABLES_220.map(c => opt(c, place.cable_220v)).join('')}</select></div>
        </div>
        <div class="spec-section">
            <h4>Ozvučení (varianta PA)</h4>
            <div class="spec-row"><label>Varianta</label><select name="pa_variant"><option value=""></option>${PA_VARIANTS.map(v => opt(v, place.pa_variant)).join('')}</select></div>
        </div>
        <div class="spec-section">
            <h4>CS10P – umístění reproduktorů TOP</h4>
            <div class="spec-row"><label>Postavení</label><select name="top_box_angle"><option value=""></option>${TOP_BOX_ANGLES.map(a => opt(a, place.top_box_angle)).join('')}</select></div>
            <div class="spec-row"><label>Doplňující info</label><input type="text" name="top_box_notes" value="${esc(place.top_box_notes || '')}" placeholder="např. (růžová), (modré, pod zábradlím)…" style="width:100%;"></div>
        </div>
        <div class="spec-section">
            <h4>SUB – fázování a umístění</h4>
            <div class="spec-row"><label>Umístění</label><select name="sub_placement"><option value=""></option>${SUB_PLACEMENTS.map(p => opt(p, place.sub_placement)).join('')}</select></div>
            <div class="spec-row"><label>Zpoždění <span class="unit">ms</span></label><input type="number" name="sub_delay_ms" value="${place.sub_delay_ms ?? ''}" min="0" placeholder="0" style="width:100px;"></div>
            <div class="spec-row"><label>Poznámka k umístění</label><input type="text" name="sub_notes" value="${esc(place.sub_notes || '')}" placeholder="např. pod jevištěm levě, na kraji…" style="width:100%;"></div>
        </div>
        <div class="spec-section">
            <h4>Stěhování aparatury</h4>
            <div class="checks">
                ${MOVING_ALL.map(o => `<label><input type="checkbox" name="moving_options" value="${esc(o)}"${moving.includes(o) ? ' checked' : ''}> ${esc(o)}</label>`).join('')}
            </div>
        </div>
        <div class="spec-section">
            <label class="check-single"><input type="checkbox" name="has_carpet"${place.has_carpet ? ' checked' : ''}> Koberec je na místě (nemusíme brát vlastní)</label>
        </div>
        <div class="spec-section">
            <h4>Poznámky a historie</h4>
            <div class="spec-row" style="grid-template-columns:1fr;">
                <textarea name="notes" rows="3" placeholder="Interní poznámky k místu (kontakt, parkování, zvláštnosti…)"
                          style="width:100%;padding:7px 10px;border:1px solid var(--border);border-radius:4px;font-family:inherit;font-size:13px;">${esc(place.notes || '')}</textarea>
            </div>
            <div class="spec-row"><label>Počet návštěv</label><input type="number" name="visit_count" value="${place.visit_count ?? 0}" min="0"></div>
            <div class="spec-row"><label>Poslední návštěva</label><input type="date" name="last_visit" value="${place.last_visit || ''}"></div>
        </div>
    `;
}

function getModal() {
    let m = document.getElementById('place-edit-modal');
    if (m) return m;
    m = document.createElement('div');
    m.id = 'place-edit-modal';
    m.className = 'modal-overlay';
    m.innerHTML = `
        <div class="modal-window">
            <div class="modal-head">
                <h2>Upravit místo: <span id="m-name"></span></h2>
                <button class="modal-close" type="button">×</button>
            </div>
            <form id="place-edit-form">
                <div class="modal-body">
                    <div class="hint">Editorské okno – tady můžeš měnit všechny parametry. Změny se uloží až po kliknutí na <em>Uložit</em>.</div>
                    <div id="m-body"></div>
                </div>
                <div class="modal-foot">
                    <button class="btn" type="button" id="m-cancel">Zrušit</button>
                    <button class="btn btn-primary" type="submit">Uložit změny</button>
                </div>
            </form>
        </div>
    `;
    document.body.appendChild(m);

    const close = () => m.classList.remove('open');
    m.querySelector('.modal-close').addEventListener('click', close);
    m.querySelector('#m-cancel').addEventListener('click', close);
    m.addEventListener('click', (e) => { if (e.target === m) close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    return m;
}

function blankPlace() {
    return {
        environment: 'uvnitř',
        distance_pilsen: 0,
        distance_stribro: 0,
        moving_options: [],
        has_carpet: false,
        visit_count: 0
    };
}

async function openEdit(place, afterSave) {
    const isNew = !place.id;
    const modal = getModal();
    modal.querySelector('.modal-head h2').innerHTML = isNew
        ? 'Nové místo'
        : `Upravit místo: <span id="m-name"></span>`;
    if (!isNew) modal.querySelector('#m-name').textContent = place.name;
    modal.querySelector('#m-body').innerHTML = renderEditForm(place);
    modal.classList.add('open');

    const form = modal.querySelector('#place-edit-form');

    // Toggle aktivace pole „Rezerva místa navíc"
    const extraActive = form.querySelector('[name="extra_reserve_active"]');
    const extraInput  = form.querySelector('[name="extra_reserve_min"]');
    if (extraActive && extraInput) {
        extraActive.addEventListener('change', () => {
            extraInput.disabled = !extraActive.checked;
            if (extraActive.checked && (parseInt(extraInput.value, 10) || 0) === 0) extraInput.value = 15;
        });
    }

    const handler = async (e) => {
        e.preventDefault();
        const fd = new FormData(form);
        const moving = fd.getAll('moving_options');
        const newName = (fd.get('name') || '').trim();
        if (!newName) { alert('Název místa nesmí být prázdný.'); return; }
        const update = {
            name: newName,
            environment: fd.get('environment'),
            distance_pilsen: parseInt(fd.get('distance_pilsen') || '0', 10),
            distance_stribro: parseInt(fd.get('distance_stribro') || '0', 10),
            travel_time_pilsen: fd.get('travel_time_pilsen') ? parseInt(fd.get('travel_time_pilsen'), 10) : null,
            travel_time_stribro: fd.get('travel_time_stribro') ? parseInt(fd.get('travel_time_stribro'), 10) : null,
            extra_reserve_min: fd.get('extra_reserve_active') === 'on'
                ? (parseInt(fd.get('extra_reserve_min'), 10) || 15)
                : 0,
            power_type: fd.get('power_type') || null,
            power_side: fd.get('power_side') || null,
            cable_400v: fd.get('cable_400v') || null,
            cable_220v: fd.get('cable_220v') || null,
            pa_variant: fd.get('pa_variant') || null,
            stribro_route:      fd.get('stribro_route') || null,
            reserve_start_min:  fd.get('reserve_start_min')  ? parseInt(fd.get('reserve_start_min'),  10) : null,
            stage_setup_min:    fd.get('stage_setup_min')    ? parseInt(fd.get('stage_setup_min'),    10) : null,
            moving_min:         fd.get('moving_min')         ? parseInt(fd.get('moving_min'),         10) : null,
            reserve_depart_min: fd.get('reserve_depart_min') ? parseInt(fd.get('reserve_depart_min'), 10) : null,
            top_box_angle: fd.get('top_box_angle') || null,
            top_box_notes: (fd.get('top_box_notes') || '').trim() || null,
            sub_placement: fd.get('sub_placement') || null,
            sub_delay_ms: fd.get('sub_delay_ms') ? parseInt(fd.get('sub_delay_ms'), 10) : null,
            sub_notes: (fd.get('sub_notes') || '').trim() || null,
            moving_options: moving,
            has_carpet: fd.get('has_carpet') === 'on',
            notes: (fd.get('notes') || '').trim() || null,
            visit_count: parseInt(fd.get('visit_count') || '0', 10),
            last_visit: fd.get('last_visit') || null,
            updated_at: new Date().toISOString()
        };
        const { error } = isNew
            ? await supabase.from('places').insert(update)
            : await supabase.from('places').update(update).eq('id', place.id);
        if (error) {
            alert('Chyba uložení: ' + error.message);
            return;
        }
        modal.classList.remove('open');
        afterSave && afterSave();
    };
    form.onsubmit = handler;
}

export async function renderMista(container) {
    container.innerHTML = '<div class="state-banner">Načítám místa…</div>';

    const [{ data: places, error }, settings, { data: photos }, admin] = await Promise.all([
        supabase.from('places').select('*').order('name'),
        getSettings(),
        supabase.from('photos').select('*').eq('type', 'tech').order('uploaded_at', { ascending: false }),
        isAdmin()
    ]);

    if (error) {
        container.innerHTML = `<div class="state-banner error">Chyba načtení: ${esc(error.message)}</div>`;
        return;
    }

    if (!places || places.length === 0) {
        container.innerHTML = '<div class="state-banner">Databáze míst je prázdná. Spusť <code>seed.sql</code> v Supabase, nebo přidej první místo.</div>';
        return;
    }

    // Pro každé místo zjisti tech fotky a vygeneruj signed URLs
    const photosByPlace = new Map();
    (photos || []).forEach(p => {
        if (!photosByPlace.has(p.place_id)) photosByPlace.set(p.place_id, []);
        photosByPlace.get(p.place_id).push(p);
    });
    for (const place of places) {
        const list = photosByPlace.get(place.id) || [];
        await Promise.all(list.map(async p => { p.url = await getSignedUrl(p.storage_path); }));
        place._photos = list;
    }

    container.innerHTML = `
        <div class="filters">
            <input class="search-input" type="search" placeholder="Hledat místo...">
            ${admin ? `<button class="btn btn-sm btn-primary" style="margin-left:auto;" id="add-place-btn">+ Nové místo</button>` : ''}
        </div>
        <div class="places-grid">
            ${places.map(p => {
                const photos = p._photos || [];
                const photoCount = photos.length;
                const env = p.environment === 'venku' ? 'venku' : 'uvnitř';

                // Kompaktní tech-summary pro hlavičku
                const cables = [];
                if (p.cable_400v && p.cable_400v !== 'žádný') cables.push(`400V ${esc(p.cable_400v)}`);
                if (p.cable_220v && p.cable_220v !== 'žádný') cables.push(`220V ${esc(p.cable_220v)}`);
                const cableTxt = cables.length ? cables.join(' + ') : '—';

                return `
                <details class="place-card" data-id="${p.id}">
                    <summary class="place-head">
                        <span class="chevron">▶</span>
                        <h3>${esc(p.name)}</h3>
                        <div class="summary-info">
                            <span class="env-tag${env === 'venku' ? ' venku' : ''}">${env === 'venku' ? 'Venku' : 'Uvnitř'}</span>
                            <span class="km" title="km od Plzně / od Stříbra">${p.distance_pilsen || 0}/${p.distance_stribro || 0} km</span>
                            <span class="sm-pa" title="Ozvučení">${esc(p.pa_variant || '—')}</span>
                            <span class="sm-power" title="Zdroj el. + strana">${esc(p.power_type || '—')}${p.power_side ? ' · ' + esc(p.power_side) : ''}</span>
                            <span class="sm-cable" title="Prodlužováky">${cableTxt}</span>
                            ${p.has_carpet ? '<span class="sm-carpet" title="Koberec je na místě – nemusíme brát vlastní">✓ Koberec</span>' : ''}
                            <span class="photo-count${photoCount === 0 ? ' empty' : ''}" title="Technické foto místa">
                                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="13" r="3"/><path d="M3 7h4l2-3h6l2 3h4v12H3z"/></svg>
                                ${photoCount}
                            </span>
                            <span style="margin-left:auto; font-size:11px; color:var(--text-muted);">${p.updated_at ? `upraveno ${new Date(p.updated_at).toLocaleDateString('cs-CZ')}` : (p.last_visit ? `posl. ${p.last_visit}` : 'nenavštíveno')}</span>
                            ${admin ? `<button class="btn btn-sm edit-place-inline" data-edit-id="${p.id}" title="Upravit místo">Upravit</button>` : ''}
                        </div>
                    </summary>
                    ${renderDisplay(p, settings)}

                    <div class="gallery-section">
                        <div class="section-head">
                            <span class="label tech">
                                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 6l4 4-8 8-4 1 1-4 8-8z M16 4l4 4"/></svg>
                                Technické foto místa
                            </span>
                            <span class="count">${photoCount} ${photoCount === 1 ? 'fotka' : (photoCount < 5 ? 'fotky' : 'fotek')}</span>
                        </div>
                        <div class="photo-thumbs tech-grid">
                            ${photos.map(ph => {
                                if (!ph.url) return `<div class="thumb">…</div>`;
                                const type = getMediaType(ph.storage_path);
                                return mediaThumbHTML({
                                    url: ph.url, path: ph.storage_path, mediaType: type,
                                    dataAttrs: `data-photo-id="${ph.id}" data-path="${esc(ph.storage_path)}"`
                                });
                            }).join('')}
                            <label class="thumb add tech" title="Nahrát foto / video / audio místa">+
                                <input type="file" accept="${ACCEPT_ALL_MEDIA}" multiple style="display:none;" data-upload-tech="${p.id}">
                            </label>
                        </div>
                    </div>

                </details>
            `;}).join('')}
        </div>

        <div class="footer-note">
            <strong>${places.length}</strong> míst v databázi.${admin ? ' Kliknutím na <em>Upravit</em> otevřeš editorské okno – změny se uloží do Supabase.' : ''}
        </div>
    `;

    // Nové místo
    const addBtn = container.querySelector('#add-place-btn');
    if (addBtn) {
        addBtn.addEventListener('click', () => openEdit(blankPlace(), () => renderMista(container)));
    }

    // Inline Upravit přímo v souhrnném řádku (vždy viditelné — i u zabaleného menu)
    container.querySelectorAll('.edit-place-inline').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const place = places.find(p => p.id === btn.dataset.editId);
            if (place) openEdit(place, () => renderMista(container));
        });
    });

    // Upload tech foto k místu
    container.querySelectorAll('[data-upload-tech]').forEach(input => {
        input.addEventListener('change', async () => {
            const placeId = input.dataset.uploadTech;
            const files = input.files;
            if (!files?.length) return;
            const card = input.closest('.place-card');
            card.style.opacity = '0.6';
            const { ok, fail, errors } = await uploadFiles(uploadTechPhoto, placeId, files);
            if (fail) {
                const detail = errors.map(e => `• ${e.name}: ${e.message}`).join('\n');
                alert(`Nahráno: ${ok}\nNepodařilo se: ${fail}\n\n${detail}`);
            }
            renderMista(container);
        });
    });

    // Klik na thumbnail → lightbox s navigací
    container.querySelectorAll('.place-card').forEach(card => {
        const placeId = card.dataset.id;
        const place = places.find(p => p.id === placeId);
        const photos = (place?._photos || []).filter(p => p.url).map(p => ({
            url: p.url,
            filename: p.storage_path.split('/').pop(),
            bucket: 'photos-tech',
            path: p.storage_path,
            photoId: p.id
        }));
        card.querySelectorAll('.thumb[data-photo-id]').forEach((el, idx) => {
            el.style.cursor = 'pointer';
            el.addEventListener('click', (e) => {
                e.stopPropagation();
                e.preventDefault();
                openLightbox({
                    photos, startIndex: idx,
                    onDelete: () => renderMista(container)
                });
            });
        });
    });

    // Vyhledávání míst
    const searchInput = container.querySelector('.search-input');
    if (searchInput) {
        searchInput.addEventListener('input', (e) => {
            const query = e.target.value.toLowerCase().trim();
            container.querySelectorAll('.place-card').forEach(card => {
                const placeName = card.querySelector('h3')?.textContent.toLowerCase() || '';
                const isVisible = placeName.includes(query) || query === '';
                card.style.display = isVisible ? '' : 'none';
            });
        });
    }
}
